import React from 'react';
import { useLoginMutation, useLoginDemoUserMutation } from '@/store/api/authApi';
import Cookies from 'js-cookie';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { loginSchema } from '@/utils/validation';
import { FloatingInput } from '@/components/common/FloatingInput';
import toast from 'react-hot-toast';
import { getPlatform } from '@/utils';

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginForm = ({
  onSwitch,
  onClose,
}: {
  onSwitch: (value: string) => void;
  onClose?: () => void;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const [loginMutation, { isLoading }] = useLoginMutation();
  const [loginDemoUser] = useLoginDemoUserMutation();

  const platform = getPlatform();

  // Submit
  const onSubmit = async (data: LoginFormData) => {
    try {
      const res = await loginMutation({
        username: data.username,
        password: data.password,
        device: platform,
      }).unwrap();
      if (res?.role?.name?.toLowerCase() !== 'user') {
        toast.error('User does not exist');
        return;
      }
      if (res?.accessToken) {
        Cookies.set('access-token', res.accessToken, { expires: 1 });
        toast.success('Login successful!');
        onClose?.();
      }
    } catch (err: any) {
      toast.error(err?.data?.message || 'Login failed!');
      console.error('Login failed:', err);
    }
  };

  // 🎮 Demo login
  const handleDemoLogin = async () => {
    try {
      const res = await loginDemoUser().unwrap();
      if (res?.accessToken) {
        Cookies.set('access-token', res.accessToken, { expires: 1 });
        toast.success('Demo login successful!');
        onClose?.();
      }
    } catch (err) {
      console.error('Demo login failed:', err);
    }
  };

  return (
    <div className="px-4 py-10 text-white">
      {/* Logo */}
      <div className="flex justify-center mb-8">
        <img src="/logo.png" alt="logo" className="w-[130px]" />
      </div>

      <form autoComplete="off" className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {/* Username */}
        <FloatingInput label="User Name" register={register('username')} error={errors.username} />
        {/* Password */}
        <FloatingInput
          label="Enter Password"
          type="password"
          register={register('password')}
          error={errors.password}
        />

        {/* Remember */}
        <div className="flex items-center justify-between text-sm text-gray-400">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div className="relative">
              <input type="checkbox" className="sr-only peer" />
              <div className="w-6 h-[14px] bg-white rounded-full peer peer-checked:bg-primary transition-colors duration-200 border border-[#adb5bd]" />
              <div className="absolute top-0.5 left-0.5 size-[10px] bg-[#adb5bd] rounded-full shadow transition-transform duration-200 peer-checked:translate-x-[10px] peer-checked:bg-black" />
            </div>
            Remember Me
          </label>
        </div>

        {/* Login */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-primary text-black font-semibold py-2 rounded-md disabled:opacity-60"
        >
          {isLoading ? 'Logging in...' : 'LOGIN'}
        </button>
        {/* Demo Login */}
        <button
          type="button"
          onClick={handleDemoLogin}
          className="w-full bg-primary text-black font-semibold py-2 rounded-md uppercase"
        >
          Login with Demo ID
        </button>
        {/* Footer */}
        <div className="text-center text-[#cbcbcb] text-xs font-extrabold space-y-2">
          <div onClick={() => onSwitch('forgot')} className="cursor-pointer underline">
            Forgot Password
          </div>
          <div>
            Don’t have an account?{' '}
            <button onClick={() => onSwitch('register')} className="underline">
              REGISTER
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
